"use client";

import { cn } from "@/lib/utils";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** Plain text from older saves → safe HTML; rich text passes through */
function toDescriptionHtml(raw: string | undefined): string {
  const s = raw?.trim() ?? "";
  if (!s) return "";
  if (/<[a-z][\s\S]*>/i.test(s)) return s;
  const escaped = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<p class="whitespace-pre-wrap">${escaped.replace(/\n/g, "<br/>")}</p>`;
}

const InstituteSection = ({ data }: { data: any }) => {
  const dispatch = useDispatch();
  /** NewPage passes the row as `data`; some callers wrap `{ data: row }` */
  const sectionData = data?.data ?? data;

  if (!sectionData) {
    return null;
  }
  if (sectionData.status === false || sectionData.status === 0) {
    return null;
  }

  const {
    title = "About",
    title_1 = "",
    title_text_color,
    title_1_color,
    description = "",
    description_text_color,
    desktop_image = "",
    mobile_image = "",
    alt_image = "",
    read_more_text = "Know More",
    read_more_color = "#FFFFFF",
    read_more_background_color,
    background_color = "",
    cta_link = "",
  } = sectionData;

  const raw = sectionData as Record<string, unknown>;
  const isCtaOn = isCmsShowFlagOn(raw?.is_cta ?? raw?.isCta, true);
  const imageOnRight = isCmsShowFlagOn(
    raw?.image_on_right ?? raw?.imageOnRight,
    false,
  );

  const href = normalizeHref(String(cta_link || ""));
  const ctaOpensLeadPopup = isCtaOn || !href;

  const openLeadFromCta = useCallback(() => {
    ctaClick({
      section: "about_3",
      component: "institute_know_more",
      label: read_more_text?.trim() || "know_more",
      step: "1",
      entity: "about_institute",
      parentEntity: "about_3",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [dispatch, read_more_text]);
  const mobileSrc = mobile_image || desktop_image;
  const desktopSrc = desktop_image || mobile_image;
  const hasImage = Boolean(mobileSrc || desktopSrc);
  const descriptionHtml = toDescriptionHtml(description);

  const ctaClass = cn(
    "focus-visible:ring-ring inline-flex items-center justify-center whitespace-nowrap text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 text-center tracking-normal rounded-md cursor-pointer px-2 py-2.5 !text-[16px] gap-2 min-[1024px]:px-[18px] min-[1024px]:py-3 min-[1024px]:mt-4 min-[1024px]:gap-2.5",
    !read_more_background_color?.trim() && "bg-primary text-white",
  );

  const ctaStyle =
    read_more_background_color?.trim() ?
      {
        backgroundColor: read_more_background_color,
        color: read_more_color,
      }
    : undefined;

  return (
    <section
      style={{
        fontFamily: "Poppins, sans-serif",
        /* Full-bleed background: pure #fff default (theme `white` token resolves to #f8f9fa) */
        backgroundColor: background_color?.trim() || "#FFFFFF",
      }}
      className={cn(
        /* Whole block: literal Poppins stack only (not --font-poppins); beat CMS inline fonts */
        "[&_*]:![font-family:Poppins,sans-serif]",
        "w-full",
      )}
    >
      <div
        className={cn(
        /* Same horizontal rhythm as NavBar3 + IIT max container (globals: --max-width-screen-container) */
        /* Mobile gap = CTA ↔ image spacing (only 2 children in the column) */
        "mx-auto flex w-full min-w-0 max-w-screen-container flex-col gap-4 px-6 pb-3 pt-10",
        "min-[1024px]:items-center min-[1024px]:gap-[74px] min-[1024px]:px-9 min-[1024px]:py-10",
        /* image_on_right=false (default) → flex-row-reverse puts image-div (declared 2nd) on the left */
        imageOnRight ? "min-[1024px]:flex-row" : "min-[1024px]:flex-row-reverse",
        )}
      >
      <div className="flex w-full min-w-0 flex-1 flex-col items-start gap-6">
        <div className="flex w-full min-w-0 flex-col gap-3">
          <h2 className="text-left text-[28px] font-semibold leading-snug tracking-normal">
            {title?.trim() ?
              <span
                style={
                  title_text_color?.trim() ?
                    { color: title_text_color }
                  : undefined
                }
              >
                {title.trim()}
              </span>
            : null}
            {title?.trim() && title_1?.trim() ? " " : null}
            {title_1?.trim() ?
              <span
                className={cn(!title_1_color?.trim() && "text-primary")}
                style={
                  title_1_color?.trim() ?
                    { color: title_1_color }
                  : undefined
                }
              >
                {title_1.trim()}
              </span>
            : null}
          </h2>
          {descriptionHtml ?
            <div
              className={cn(
                "min-w-0 max-w-none text-left [overflow-wrap:anywhere] min-[1024px]:text-justify",
                /* iitkgp parity: mobile body-5 (14px/1.5, default color); desktop body-1 (18px) + gray */
                "text-[14px] leading-normal min-[1024px]:text-[18px] min-[1024px]:leading-relaxed",
                "[&_p]:whitespace-pre-wrap [&_p+_p]:mt-3",
                /* CMS color set → force inherit so rich-text inline colors don't win; else gray on desktop only */
                description_text_color?.trim() ?
                  "[&_*]:![color:inherit]"
                : "min-[1024px]:text-gray-500",
              )}
              style={
                description_text_color?.trim() ?
                  { color: description_text_color }
                : undefined
              }
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          : null}
        </div>

        <div className="flex flex-col gap-3" aria-hidden />

        {read_more_text?.trim() && ctaOpensLeadPopup ? (
          <button
            type="button"
            className={ctaClass}
            style={ctaStyle}
            onClick={openLeadFromCta}
          >
            <span>{read_more_text}</span>
            <ArrowUpRight className="size-6 shrink-0" strokeWidth={2} />
          </button>
        ) : read_more_text?.trim() && href ? (
          <Link
            href={href}
            className={ctaClass}
            style={ctaStyle}
            onClick={() =>
              ctaClick({
                section: "about_3",
                component: "institute_know_more_link",
                label: read_more_text?.trim() || "know_more",
                step: "1",
                entity: "about_institute",
                parentEntity: "about_3",
              })
            }
          >
            <span>{read_more_text}</span>
            <ArrowUpRight className="size-6 shrink-0" strokeWidth={2} />
          </Link>
        ) : read_more_text?.trim() ? (
          <button
            type="button"
            className={ctaClass}
            style={ctaStyle}
            onClick={openLeadFromCta}
          >
            <span>{read_more_text}</span>
            <ArrowUpRight className="size-6 shrink-0" strokeWidth={2} />
          </button>
        ) : null}
      </div>

      {hasImage ?
        <div
          className={cn(
            /* Mobile: 327×340 box, centered; max-w so it never overflows px-6 below 375px */
            "relative h-[340px] w-full max-w-[327px] shrink-0 self-center",
            "min-[1024px]:h-[392px] min-[1024px]:w-[372px] min-[1024px]:max-w-none min-[1024px]:self-auto",
          )}
        >
          {mobileSrc ?
            <Image
              alt={alt_image || "About"}
              src={mobileSrc}
              fill
              sizes="327px"
              className="rounded-tr-[34px] rounded-bl-[34px] object-contain object-center min-[1024px]:hidden"
            />
          : null}
          {desktopSrc ?
            <Image
              alt={alt_image || "About"}
              src={desktopSrc}
              fill
              sizes="(min-width: 1024px) 372px, 100vw"
              className="hidden rounded-tr-[40px] rounded-bl-[40px] object-contain object-center min-[1024px]:block"
            />
          : null}
        </div>
      : null}
      </div>
    </section>
  );
};

export default InstituteSection;
